import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { authAxios as ax } from "@/contexts/AuthContext";
import axios from "axios";
import { Bell, AlertTriangle, AlertCircle, Info, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityConfig = {
  critical: { icon: AlertCircle, color: "text-[#FF2A2A]", bg: "bg-[#FF2A2A]/5 border-[#FF2A2A]/20" },
  warning: { icon: AlertTriangle, color: "text-[#FFB800]", bg: "bg-[#FFB800]/5 border-[#FFB800]/20" },
  info: { icon: Info, color: "text-[#002FA7]", bg: "bg-[#002FA7]/5 border-[#002FA7]/20" },
};

export default function NotificationBell() {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await ax.get(`${API}/notifications`);
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  const markRead = async (id) => {
    await ax.put(`${API}/notifications/${id}/read`).catch(() => {});
    load();
  };

  const markAllRead = async () => {
    await ax.put(`${API}/notifications/read-all`).catch(() => {});
    load();
  };

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-1.5 text-[#4B5563] hover:text-[#0A0A0A] transition-colors" data-testid="notification-bell">
          <Bell size={18} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center bg-[#FF2A2A] text-white text-[9px] font-bold rounded-full" data-testid="notification-badge">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 rounded-none p-0" align="end" data-testid="notification-panel">
        <div className="flex items-center justify-between p-3 border-b border-[#E5E7EB]">
          <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#0A0A0A]">{t("notifications.title")}</p>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="h-6 text-[10px] font-bold text-[#002FA7]" onClick={markAllRead} data-testid="mark-all-read">
              {t("notifications.mark_all_read")}
            </Button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto thin-scrollbar">
          {notifications.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-6">{t("notifications.empty")}</p>
          ) : (
            notifications.map((n) => {
              const cfg = severityConfig[n.severity] || severityConfig.info;
              const Icon = cfg.icon;
              return (
                <div
                  key={n.notification_id}
                  className={`flex gap-2.5 p-3 border-b border-[#E5E7EB] cursor-pointer transition-colors hover:bg-[#F8F9FA] ${!n.read ? "bg-[#F8F9FA]" : ""}`}
                  onClick={() => !n.read && markRead(n.notification_id)}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center shrink-0 border ${cfg.bg}`}>
                    <Icon size={14} className={cfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <p className={`text-xs ${!n.read ? "font-bold text-[#0A0A0A]" : "font-medium text-[#4B5563]"} truncate`}>{n.title}</p>
                      {!n.read && <span className="w-1.5 h-1.5 bg-[#002FA7] rounded-full shrink-0 mt-1" />}
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] line-clamp-2 mt-0.5">{n.message}</p>
                    <p className="text-[9px] text-[#9CA3AF] mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
